'use client';

import { useCallback, useState } from 'react';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import QuantityWidget from '@/components/atoms/quantity-widget';
import Badge from '@/components/atoms/badge';
import HintTooltip from '@/components/atoms/hint-tooltip';
import useFormat from '@/hooks/useFormat';
import { resolveStockStatus } from '@/utils/product/resolve-stock-status';
import { resolveAddToCartAvailability } from '@/utils/product/add-to-cart-availability';
import { isBackorderRestricted } from '@/utils/isBackorderRestricted';
import ItemMenu from './components/item-menu';
import { PurchaseListItemProps } from './types';

const PurchaseListItem = ({
  item,
  viewMode,
  backOrdersAllowed,
  paymentTerms,
  onRemove,
  onAddToCart,
  onMove,
  onQuantityChange,
}: PurchaseListItemProps) => {
  const translate = useTranslations();
  const { formatCurrency } = useFormat();
  const [addingToCart, setAddingToCart] = useState(false);

  // EWM-554 — `item.quantity` is the user's ephemeral selection for "Add (all) to
  // cart", owned and reset to 0 by the page (NOT persisted to the wishlist, since
  // commercetools rejects qty 0 — see EWM-384). The stepper is controlled and floors
  // at 0 so the user toggles up only what they want; cart-add is gated until > 0.
  const handleAddToCart = useCallback(async () => {
    setAddingToCart(true);
    await onAddToCart?.();
    setAddingToCart(false);
  }, [onAddToCart]);

  const handleRemove = useCallback(async () => {
    await onRemove?.();
  }, [onRemove]);

  const isImageMode = viewMode === 'image';

  // EWM-1090 — the row now applies the SAME add-to-cart rule as the PDP/PLP
  // (`resolveAddToCartAvailability`, EWM-360), fed the customer's back-order
  // entitlement. An entitled account can add an out-of-stock item as a back-order;
  // GO ("removed from sale") and no-price stay disabled for everyone. The
  // entitlement is the FULL cash-aware rule the checkout gate enforces
  // (`isBackorderRestricted`, EWM-641) — a cash account cannot back-order even
  // when its BU allows it — mirroring the uploader (`upload-line-availability.ts`).
  const entitled = !isBackorderRestricted({ backOrdersAllowed, paymentTerms });
  const { disabled: stockGateDisabled, labelState } = resolveAddToCartAvailability({
    inStock: item.inStock,
    priceUnavailable: item.priceUnavailable,
    removedFromSale: item.removedFromSale,
    backOrdersAllowed: entitled,
  });
  const isBackOrder = labelState === 'back-order';

  // EWM-756 / EWM-1090 — explain *why* the Add-to-Cart button is disabled. The
  // stock resolver distinguishes "Stock due soon" (new/pending) from a plain
  // "Out of stock"; GO and no-price get their own reasons; the quantity gate
  // (EWM-554) is the last reason. Empty string = addable, no tooltip.
  const stockStatus = resolveStockStatus({
    inStock: item.inStock,
    isStockDueSoonEligible: item.isStockDueSoonEligible,
  });
  const disabledReason = item.priceUnavailable
    ? translate('product.price-unavailable')
    : item.removedFromSale
      ? // EWM-1090 R1-2 — mirror the uploader precedent (`upload-line-availability.ts`):
        // an out-of-stock GO item might reasonably be mistaken for a back-order, so
        // naming back-order is useful; an in-stock GO item is not a back-order case
        // at all — "cannot be back-ordered" reads as a bug, so say it is no longer sold.
        item.inStock
        ? translate('quick-order.removed-from-sale')
        : translate('quick-order.excluded-from-back-order')
      : stockGateDisabled && stockStatus === 'due-soon'
        ? translate('product.stock-due-soon')
        : stockGateDisabled && stockStatus === 'out-of-stock'
          ? translate('common.out-of-stock')
          : item.quantity <= 0
            ? translate('wishlist.enter-quantity-to-add')
            : '';

  // EWM-1090 — the button is disabled by the shared stock/price/GO gate, an
  // in-flight add, or an unselected quantity. When enabled but out of stock, the
  // account is entitled to back-order it → the button relabels "Back Order"
  // (matching the PDP), otherwise "Add to cart".
  const addDisabled = stockGateDisabled || addingToCart || item.quantity <= 0;
  const accessibleName = disabledReason || (isBackOrder ? translate('pdp.back-order') : translate('cart.add'));

  return (
    <article
      className={[
        'relative flex rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-sm',
        isImageMode ? 'flex-col md:flex-row md:items-stretch' : 'flex-row items-center',
      ].join(' ')}
    >
      {/* Product image — image view only */}
      {isImageMode && (
        <Link href={item.url} className="block w-full shrink-0 md:w-auto">
          <div className="flex h-40 w-full items-center justify-center rounded-t-lg bg-gray-50 p-4 md:h-[160px] md:w-40 md:rounded-l-lg md:rounded-tr-none">
            <Image
              src={item.image}
              alt={item.name}
              width={128}
              height={128}
              className="mx-auto max-h-full max-w-full object-contain mix-blend-multiply"
            />
          </div>
        </Link>
      )}

      {/* Content — p-4 always; py-3 overrides vertical padding in list mode */}
      <div
        className={[
          'flex flex-1 gap-4 p-4',
          isImageMode ? 'flex-col md:flex-row md:items-center' : 'flex-row items-center py-3',
        ].join(' ')}
      >
        {/* Identity */}
        <div className="min-w-0 flex-1">
          {(item.brand || item.badge) && (
            <div className="flex items-center gap-2">
              {item.brand && (
                <span className="text-11 font-semibold tracking-wide text-gray-500 uppercase">{item.brand}</span>
              )}
              {item.badge && <Badge variant={item.badge}>{item.badge.replace(/-/g, ' ')}</Badge>}
            </div>
          )}
          <Link href={item.url}>
            <h3 className="text-15 text-primary mt-1 line-clamp-2 font-semibold hover:underline">{item.name}</h3>
          </Link>
          <div className="text-12 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500">
            <span>
              {translate('common.cat-no')}: <span className="font-semibold text-gray-700">{item.sku}</span>
            </span>
            {item.mpn && (
              <span>
                {translate('common.mpn-prefix')} <span className="font-semibold text-gray-700">{item.mpn}</span>
              </span>
            )}
          </div>
        </div>

        {/* Price */}
        <div className="shrink-0 md:w-32 md:text-right">
          {/* EWM-1090 R1-4 — when CT returned no price the mapper carries a `0`
              fallback; rendering "$0.00" next to a disabled "Unavailable" button is
              misleading. Mirror the PLP (`product-tile-v2`) and replace the price
              with the "Unavailable" label so the row never shows a phantom $0.00. */}
          {item.priceUnavailable ? (
            <span className="text-15 font-semibold text-gray-500">{translate('product.price-unavailable')}</span>
          ) : (
            <div className="flex items-baseline gap-2 md:justify-end">
              <span className="text-16 text-primary font-bold">
                {formatCurrency(item.discountedPrice ?? item.price, item.currency)}
              </span>
              {item.discountedPrice !== undefined && (
                <span className="text-12 text-gray-400 line-through">{formatCurrency(item.price, item.currency)}</span>
              )}
            </div>
          )}
        </div>

        {/* Qty + cart + menu */}
        <div className="flex shrink-0 items-center gap-2">
          {/* EWM-1090 R1-1 — a VISIBLE "Back Order" affordance for the enabled
              out-of-stock state. The row's Add-to-Cart control is icon-only, so
              without this a sighted, entitled dealer would see an ordinary cart
              icon on an out-of-stock row and commit a back-order with no signal.
              Mirrors the PDP/PLP visible "Back Order" label (same `pdp.back-order`
              key). Only the enabled back-order state renders it; disabled states
              still explain themselves via the button tooltip/accessible name. */}
          {isBackOrder && (
            <span className="text-12 font-semibold whitespace-nowrap text-amber-600">
              {translate('pdp.back-order')}
            </span>
          )}
          <span className="inline-block w-32">
            <QuantityWidget
              value={item.quantity}
              onChange={(qty) => onQuantityChange?.(qty)}
              minValue={0}
              variant="primary"
              showLabel={false}
              spinbutton
            />
          </span>

          {(() => {
            const addToCartButton = (
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={addDisabled}
                aria-label={accessibleName}
                className="bg-primary hover:bg-primary/90 inline-flex items-center justify-center rounded-lg p-2.5 text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                <ShoppingCartIcon aria-hidden className="h-5 w-5" />
              </button>
            );
            return disabledReason ? (
              <HintTooltip message={disabledReason} position="top">
                {addToCartButton}
              </HintTooltip>
            ) : (
              addToCartButton
            );
          })()}

          <ItemMenu onMove={() => onMove?.()} onRemove={handleRemove} />
        </div>
      </div>
    </article>
  );
};

export default PurchaseListItem;
